import React, { useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { CompanyPreviewOutDTO, InvoiceRebillingTypeEnum } from '@api/ringil-api.v2.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Field } from '@components/framework/formfield/Field.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import MultipleValues from '@components/ringil3/HelperComponents/MultipleValues.tsx';
import InputCompanyPartnerPreview from '@components/obsolete/Form/InputPartner/InputCompanyPartnerPreview.tsx';
import InputDepartmentPlain from '@components/obsolete/Form/InputDepartmentPlain.tsx';
import { toTestIdSlug } from '@utils/testUtils';

export interface RebillingType {
  invoiceRebillingType: InvoiceRebillingTypeEnum;
  invoicePartner?: CompanyPreviewOutDTO;
  invoiceDepartment?: string;
}

interface ComponentProps {
  secured?: SecuredConfig;
  value: RebillingType;
  required?: boolean;
  readonly?: boolean;
  onChange: (value: RebillingType) => void;
}

const InternalInfoRebillingTag: React.FC<ComponentProps> = ({ secured, readonly, required, value, onChange }) => {
  const [internalValue, setInternalValue] = useState(value);
  const { tr } = useTranslationLgs();
  const [validationError, setValidationError] = useState<string | null>(null);
  const options = [
    InvoiceRebillingTypeEnum.NON_REBILLING,
    InvoiceRebillingTypeEnum.INTERNAL_REBILLING,
    InvoiceRebillingTypeEnum.EXTERNAL_REBILLING,
    InvoiceRebillingTypeEnum.OTHER,
  ];

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const applyOverlayTestIds = () => {
    const panel = document.querySelector('.p-dropdown-panel.reaccountToPanel') as HTMLElement | null;
    if (!panel) return;

    panel.setAttribute('data-testid', 'internalInfo-reaccountToList-overlay');
  };

  const renderText = (value: RebillingType) => {
    if (isNullOrUndefined(value)) return null;

    if (value.invoiceRebillingType === InvoiceRebillingTypeEnum.INTERNAL_REBILLING) {
      return <MultipleValues
        values={[tr(`InternalInfoRebillingTag.${value.invoiceRebillingType}`, value.invoiceRebillingType), value.invoiceDepartment]} />;
    }

    if (value.invoiceRebillingType === InvoiceRebillingTypeEnum.EXTERNAL_REBILLING) {
      return <MultipleValues
        values={[tr(`InternalInfoRebillingTag.${value.invoiceRebillingType}`, value.invoiceRebillingType), value.invoicePartner?.companyName]} />;
    }

    return tr(`InternalInfoRebillingTag.${value.invoiceRebillingType}`, value.invoiceRebillingType);
  };

  return (
    <>
      <ButtonTag secured={secured}
        label={tr("InternalInfoRebillingTag.preuctovani", "Přeúčtování")}
        required={required}
        readonly={readonly}
        value={renderText(value)}
        onSave={(onSuccess) => {
          if (
            internalValue.invoiceRebillingType === InvoiceRebillingTypeEnum.EXTERNAL_REBILLING &&
            !internalValue.invoicePartner
          ) {
            setValidationError(tr('InternalInfoRebillingTag.partnerRequired', 'Partner is mandatory'));
            return;
          }
          if (
            internalValue.invoiceRebillingType === InvoiceRebillingTypeEnum.INTERNAL_REBILLING &&
            !internalValue.invoiceDepartment
          ) {
            setValidationError(tr('InternalInfoRebillingTag.invoiceDepartment', 'Invoice department is mandatory'));
            return;
          }
          if (!internalValue?.invoiceRebillingType) {
            onChange(null);
          } else {
            onChange(internalValue);
          }
          onSuccess();
        }}
        popupWidth={'400px'}
        autoClose
        dataTestId="internalInfo-reaccountTo"
      >

        <>
          <Field label={tr("InternalInfoRebillingTag.preuctovat", "Přeúčtovat")}>
            <div data-testid="internalInfo-reaccountToList">
              <Dropdown
                value={internalValue?.invoiceRebillingType}
                className="w-full"
                options={options}
                showClear={true}
                panelClassName="reaccountToPanel"
                valueTemplate={(e) => <div>{tr(`UpdateExpeditionInternalInformationSidebar.InvoiceRebillingTypeEnum.${e}`, e)}</div>}
                itemTemplate={(e) => (
                  <div data-testid={`internalInfo-reaccountToList-${toTestIdSlug(e)}`}>
                    {tr(`UpdateExpeditionInternalInformationSidebar.InvoiceRebillingTypeEnum.${e}`, e)}
                  </div>
                )}
                onShow={() => {
                  requestAnimationFrame(applyOverlayTestIds);
                }}
                onChange={(e) => {
                  setValidationError(null);
                  setInternalValue(() => ({
                    invoiceRebillingType: e.target.value,
                  }));
                }}
              />
            </div>
          </Field>

          {internalValue?.invoiceRebillingType === InvoiceRebillingTypeEnum.INTERNAL_REBILLING &&
            <Field
              label={tr('InternalInfoRebillingTag.interniOddeleni', 'Interní oddělení')}
              required
              errorMessage={validationError}
            >
              <InputDepartmentPlain
                department={internalValue.invoiceDepartment}
                style={{ width: '100%', padding: '0', margin: '0px', backgroundColor: 'red' }}
                onChange={(v) => {
                  setValidationError(null);
                  setInternalValue(() => ({
                    invoiceRebillingType: InvoiceRebillingTypeEnum.INTERNAL_REBILLING,
                    invoiceDepartment: v,
                  }));
                }}
              />

            </Field>
          }
          {internalValue?.invoiceRebillingType === InvoiceRebillingTypeEnum.EXTERNAL_REBILLING &&
            <Field
              label={tr('InternalInfoRebillingTag.partner', 'Partner')}
              required
              errorMessage={validationError}
            >
              <InputCompanyPartnerPreview
                style={{ width: '100%' }}
                value={internalValue.invoicePartner}
                onChange={(v) => {
                  setValidationError(null);
                  setInternalValue(() => ({
                    invoiceRebillingType: InvoiceRebillingTypeEnum.EXTERNAL_REBILLING,
                    invoicePartner: v,
                  }));
                }}
              />

            </Field>
          }
        </>
      </ButtonTag>
    </>);

};

export default InternalInfoRebillingTag;